import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Check } from "lucide-react";
import { siteConfig } from "../mock";
import { useToast } from "../hooks/use-toast";
import { fetchKeyState, startAccess } from "../lib/keyApi";
import { useGate, useCosmeticRotatingUrl } from "../lib/gate";

const pad = (n) => String(n).padStart(2, "0");

const formatTime = (totalSeconds) => {
  const s = Math.max(0, totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return { h: pad(h), m: pad(m), s: pad(sec) };
};

const SCHEDULE_SLOTS = [6, 10, 14, 18, 22, 2]; // 6AM 10AM 2PM 6PM 10PM 2AM
const SCHEDULE_LABELS = ["6AM", "10AM", "2PM", "6PM", "10PM", "2AM"];

const RESYNC_INTERVAL_MS = 30_000; // reconsulta al Worker cada 30s

const KeyDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [keyData, setKeyData] = useState(null);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const pollRef = useRef(null);

  // true = viene referido de un acortador válido, o ya tiene sesión
  // reciente marcada. false = no viene de ningún sitio válido -> fuera.
  // null = todavía comprobando.
  const allowed = useGate();

  useEffect(() => {
    if (allowed === false) {
      navigate("/", { replace: true });
    }
  }, [allowed, navigate]);

  // Si vuelves con el botón "atrás" y el navegador restaura la página
  // desde caché (bfcache), fuerza recargar para re-comprobar el gate
  // desde cero en vez de enseñar la vista vieja guardada.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Si la key cambia mientras la tienes abierta (rotación), te saca
  // a la página principal automáticamente.
  const initialKeyRef = useRef(null);
  useEffect(() => {
    if (!keyData) return;
    if (initialKeyRef.current === null) {
      initialKeyRef.current = keyData.key;
    } else if (keyData.key !== initialKeyRef.current) {
      navigate("/", { replace: true });
    }
  }, [keyData, navigate]);

  // Efecto cosmético: reescribe un parámetro sin sentido en la URL
  // cada pocos milisegundos para que un enlace copiado en ese instante
  // no sirva compartido más tarde (no es la validación real).
  useCosmeticRotatingUrl(allowed === true);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  const accessTokenRef = useRef(null);

  const sync = useCallback(async () => {
    try {
      if (!accessTokenRef.current) {
        const { token } = await startAccess();
        accessTokenRef.current = token;
      }
      const data = await fetchKeyState(accessTokenRef.current);
      setKeyData(data);
      setRemaining(data.nextResetSeconds);
      setError(null);
    } catch (e) {
      // el token pudo caducar entre resincronizaciones; se pide otro la próxima vez
      accessTokenRef.current = null;
      setError("No se pudo conectar con el servidor de keys.");
    }
  }, []);

  // primera carga + resincronización periódica con el Worker
  // (solo una vez confirmado el acceso)
  useEffect(() => {
    if (allowed !== true) return;
    sync();
    pollRef.current = setInterval(sync, RESYNC_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [sync, allowed]);

  // countdown visual local, un segundo a la vez, entre resincronizaciones
  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const { h, m, s } = formatTime(remaining);

  const activeSlotIdx = keyData
    ? SCHEDULE_SLOTS.reduce(
        (best, hr, idx) =>
          Math.abs(hr - keyData.rotateHour) < Math.abs(SCHEDULE_SLOTS[best] - keyData.rotateHour)
            ? idx
            : best,
        0
      )
    : -1;

  const handleCopy = useCallback(async () => {
    if (!keyData) return;
    try {
      await navigator.clipboard.writeText(keyData.key);
    } catch (e) {
      // clipboard may be blocked; ignore
    }
    setCopied(true);
    toast({ title: "Key copiada", description: keyData.key });
    setTimeout(() => setCopied(false), 2000);

    // 10 minutos después de copiar, de vuelta a la página principal
    setTimeout(() => {
      navigate("/", { replace: true });
    }, 10 * 60 * 1000);
  }, [toast, keyData, navigate]);

  if (allowed !== true) {
    return <div className="stark-root" />;
  }

  if (error) {
    return (
      <div className="stark-root">
        <main className="stark-center">
          <p className="kd-rotates">{error}</p>
        </main>
      </div>
    );
  }

  if (!keyData) {
    return (
      <div className="stark-root">
        <main className="stark-center">
          <p className="kd-rotates">Cargando key...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="stark-root">
      <div className="stark-glow stark-glow--tl" />
      <div className="stark-glow stark-glow--br" />
      <div className="stark-grain" />

      <main className="stark-center">
        <section className={`kd-card ${mounted ? "is-in" : ""}`}>
          <h1 className="stark-title kd-title">{siteConfig.title}</h1>

          {/* key + copy */}
          <div className="kd-keyrow">
            <span className="kd-key">{keyData.key}</span>
            <button
              type="button"
              className="kd-copy"
              onClick={handleCopy}
              aria-label="Copy key"
            >
              {copied ? (
                <>
                  <Check size={15} strokeWidth={2.6} />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={15} strokeWidth={2.2} />
                  Copy
                </>
              )}
            </button>
          </div>

          {/* next key timer panel */}
          <div className="kd-timer">
            <div className="kd-timer__top">
              <span className="kd-timer__label">Next Key</span>
              <span className="kd-timer__clock">
                {h}
                <span className="kd-colon">:</span>
                {m}
                <span className="kd-colon">:</span>
                {s}
              </span>
            </div>

            <div className="kd-progress">
              <div className="kd-progress__track">
                <div
                  className="kd-progress__fill"
                  style={{ width: `${keyData.progress}%` }}
                />
              </div>
              <span className="kd-progress__pct">{keyData.progress}%</span>
            </div>

            <p className="kd-rotates">
              Rotates at <strong>{keyData.rotatesAt}</strong>
            </p>
          </div>

          {/* daily schedule */}
          <div className="kd-schedule">
            <span className="kd-schedule__title">Daily Schedule</span>
            <div className="kd-slots">
              {SCHEDULE_LABELS.map((label, idx) => (
                <span
                  key={label}
                  className={`kd-slot ${idx === activeSlotIdx ? "is-active" : ""}`}
                >
                  {label}
                </span>
              ))}
            </div>
            <span className="kd-localtime">Your local time</span>
          </div>

          <div className="kd-divider" />

          <div className="kd-footer">
            <span className="kd-updated">Updated: {keyData.updatedAgo}</span>
            {keyData.live && (
              <span className="kd-live">
                <span className="kd-live__dot" />
                Live
              </span>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default KeyDashboard;
